import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { accountsApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Spinner, Alert, EmptyState } from '@rankhelm/ui';
import type { Account } from '@rankhelm/shared';

export function AccountsPage() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<'agency' | 'individual' | 'enterprise'>('agency');
  const [creating, setCreating] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    loadAccounts();
  }, []);

  async function loadAccounts() {
    try {
      const res = await accountsApi.list();
      // API returns array for super_admin, single object for others
      const data = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      setAccounts(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      await accountsApi.create({ name: newName.trim(), type: newType });
      setNewName('');
      setShowCreate(false);
      setSuccess('Account created successfully');
      await loadAccounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setCreating(false);
    }
  }

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-400">You don't have permission to manage accounts.</p>
      </div>
    );
  }

  if (loading) return <Spinner message="Loading accounts..." fullPage />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Each account represents an agency, individual user, or enterprise on RankHelm.</p>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="px-4 py-2.5 bg-helm-500 hover:bg-helm-400 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {showCreate ? 'Cancel' : '+ New Account'}
        </button>
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Create Account Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-navy-900 border border-navy-700 rounded-xl p-6 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Account Name</label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500"
              placeholder="e.g., Acme Digital Agency"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Account Type</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value as 'agency' | 'individual' | 'enterprise')}
              className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500"
            >
              <option value="agency">Agency — White-label, client management</option>
              <option value="individual">Individual — Standard Rank Helm branding</option>
              <option value="enterprise">Enterprise — Custom plan limits</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={creating || !newName.trim()}
            className="px-5 py-2.5 bg-success hover:bg-green-400 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {creating ? 'Creating...' : 'Create Account'}
          </button>
        </form>
      )}

      {/* Account List */}
      {accounts.length === 0 ? (
        <EmptyState message="No accounts yet. Create your first account to get started." />
      ) : (
        <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-700">
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3">Name</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden sm:table-cell">Acct ID</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden lg:table-cell">Slug</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden sm:table-cell">Type</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden md:table-cell">Plan</th>
                <th className="text-left text-xs font-medium text-slate-400 px-5 py-3 hidden md:table-cell">Created</th>
                <th className="px-5 py-3"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((acct) => (
                <tr key={acct.id} className="border-b border-navy-700/50 hover:bg-navy-800/50 transition-colors">
                  <td className="px-5 py-4">
                    <Link to={`/accounts/${acct.id}`} className="text-white hover:text-helm-400 font-medium text-sm transition-colors">
                      {acct.name}
                    </Link>
                    <p className="text-xs text-slate-500 mt-0.5 sm:hidden">
                      {acct.display_id ? `${acct.display_id} · ` : ''}{acct.slug}
                    </p>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    {acct.display_id ? (
                      <span className="text-xs font-mono text-slate-300">{acct.display_id}</span>
                    ) : (
                      <span className="text-xs text-slate-600">—</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-slate-400 text-sm hidden lg:table-cell">{acct.slug}</td>
                  <td className="px-5 py-4 hidden sm:table-cell">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      acct.type === 'agency' ? 'bg-purple-500/20 text-purple-400' : acct.type === 'enterprise' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {acct.type || 'individual'}
                    </span>
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-helm-500/20 text-helm-400">
                      {acct.plan_tier || 'free'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-slate-500 text-xs hidden md:table-cell">
                    {acct.created_at ? new Date(acct.created_at).toLocaleDateString() : '--'}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link
                      to={`/accounts/${acct.id}`}
                      className="text-xs text-helm-400 hover:text-helm-300 transition-colors"
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
